import React, { useState } from 'react';
import { Moon, Cloud, Star, X, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import MoonClock from './MoonClock';
import WeatherWidget from './WeatherWidget';
import AstrologyWidget from './AstrologyWidget';

const CosmicSidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('moon');

  const tabs = [
    { id: 'moon', icon: Moon, label: 'Lunar', color: '#c9a96e' },
    { id: 'weather', icon: Cloud, label: 'Weather', color: '#00B4D8' },
    { id: 'astrology', icon: Star, label: 'Zodiac', color: '#9D4EDD' },
  ];

  return (
    <>
      {/* Toggle Button - Always Visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          fixed right-0 top-1/2 -translate-y-1/2 z-50
          flex items-center gap-2 px-3 py-4
          bg-gradient-to-b from-[#1e1919] to-[#2a2525] text-white
          rounded-l-xl shadow-2xl
          transition-all duration-300 hover:px-4
          ${isOpen ? 'translate-x-full opacity-0 pointer-events-none' : ''}
        `}
        data-testid="cosmic-sidebar-toggle"
      >
        <div className="flex flex-col items-center gap-2">
          <Sparkles className="w-5 h-5 text-[#c9a96e]" />
          <span className="text-xs font-semibold tracking-wider writing-mode-vertical" style={{ writingMode: 'vertical-rl' }}>
            COSMIC
          </span>
          <ChevronLeft className="w-4 h-4" />
        </div>
      </button>

      {/* Sidebar Panel */}
      <div
        className={`
          fixed right-0 top-0 h-full w-[380px] z-50
          transform transition-transform duration-500 ease-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
        data-testid="cosmic-sidebar-panel"
      >
        {/* Background with blur */}
        <div className="absolute inset-0 bg-[#faf8f5]/95 backdrop-blur-xl border-l border-[#9D4EDD]/20 shadow-2xl" />
        
        {/* Content */}
        <div className="relative h-full flex flex-col">
          {/* Header */}
          <div className="p-4 border-b border-[#9D4EDD]/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#9D4EDD] to-[#FF6B9D] flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="font-serif text-lg text-[#1e1919]">Cosmic Guide</h2>
                <p className="text-xs text-[#7A7A9A]">Align your crystal journey</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 rounded-lg hover:bg-[#9D4EDD]/10 transition-colors"
              data-testid="cosmic-sidebar-close"
            >
              <X className="w-5 h-5 text-[#7A7A9A]" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="p-3 border-b border-[#9D4EDD]/10">
            <div className="flex gap-2 bg-white/50 rounded-xl p-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg
                    text-xs font-semibold uppercase tracking-wider
                    transition-all duration-300
                    ${activeTab === tab.id
                      ? 'bg-gradient-to-r from-[#1e1919] to-[#2a2525] text-white shadow-lg'
                      : 'text-[#7A7A9A] hover:text-[#1e1919] hover:bg-white/50'
                    }
                  `}
                  data-testid={`cosmic-tab-${tab.id}`}
                >
                  <tab.icon className="w-4 h-4" style={{ color: activeTab === tab.id ? tab.color : 'inherit' }} />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className={`transition-opacity duration-300 ${activeTab === 'moon' ? 'opacity-100' : 'opacity-0 hidden'}`}>
              <MoonClock />
            </div>
            <div className={`transition-opacity duration-300 ${activeTab === 'weather' ? 'opacity-100' : 'opacity-0 hidden'}`}>
              <WeatherWidget />
            </div>
            <div className={`transition-opacity duration-300 ${activeTab === 'astrology' ? 'opacity-100' : 'opacity-0 hidden'}`}>
              <AstrologyWidget />
            </div>
          </div>

          {/* Footer CTA */}
          <div className="p-4 border-t border-[#9D4EDD]/10 bg-gradient-to-r from-[#9D4EDD]/5 to-[#FF6B9D]/5">
            <p className="text-xs text-center text-[#7A7A9A] mb-2">
              Let the cosmos guide your crystal selection
            </p>
            <a
              href="/shop"
              className="block w-full py-3 px-4 text-center bg-gradient-to-r from-[#9D4EDD] to-[#FF6B9D] text-white text-sm font-semibold rounded-xl hover:shadow-lg hover:shadow-[#9D4EDD]/30 transition-all"
              data-testid="cosmic-shop-cta"
            >
              Explore Crystal Shop
            </a>
          </div>
        </div>
      </div>

      {/* Overlay when sidebar is open */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
          onClick={() => setIsOpen(false)}
          data-testid="cosmic-sidebar-overlay"
        />
      )}
    </>
  );
};

export default CosmicSidebar;
